import React, { useEffect, useMemo, useState } from 'react';
import { Type, Check, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from '../../lib/i18n';
import {
  FONT_PRESETS,
  FONT_SCALE_MAX,
  FONT_SCALE_MIN,
  FONT_SCALE_STEP,
  clampScale,
  formatScalePercent,
  presetMetrics,
  readFontPreference,
  type FontPreference,
  type FontSizePreset,
} from '../../lib/fontSettings';
import { useFontSettings } from '../../lib/FontContext';

const DEFAULT_PREF: FontPreference = { mode: 'preset', preset: 'comfortable' };

const FontSettings: React.FC = () => {
  const { t } = useTranslation();
  const { fontPreference, setFontPreset, setCustomScale } = useFontSettings();
  const [draft, setDraft] = useState<FontPreference>(() => readFontPreference());

  useEffect(() => {
    setDraft(fontPreference);
  }, [fontPreference]);

  const draftScale = useMemo(() => {
    if (draft.mode === 'custom') return draft.scale;
    return presetMetrics(draft.preset).scale;
  }, [draft]);

  const savedScale = useMemo(() => {
    if (fontPreference.mode === 'custom') return fontPreference.scale;
    return presetMetrics(fontPreference.preset).scale;
  }, [fontPreference]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(fontPreference);

  const selectPreset = (preset: FontSizePreset) => {
    setDraft({ mode: 'preset', preset });
  };

  const onSlider = (value: number) => {
    const scale = clampScale(value);
    const preset = FONT_PRESETS.find((p) => Math.abs(p.scale - scale) < 0.005);
    if (preset) {
      setDraft({ mode: 'preset', preset: preset.id });
    } else {
      setDraft({ mode: 'custom', scale });
    }
  };

  const save = () => {
    if (draft.mode === 'custom') setCustomScale(draft.scale);
    else setFontPreset(draft.preset);
    toast.success(t('settings.fontSaved'));
  };

  const reset = () => {
    setDraft(DEFAULT_PREF);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50/80 to-white p-3 sm:rounded-xl sm:p-5">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 sm:h-11 sm:w-11 sm:rounded-xl">
            <Type className="h-4 w-4 sm:h-5 sm:w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <h3 className="text-sm font-semibold text-zinc-900 sm:text-base">{t('settings.fontTitle')}</h3>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 sm:text-xs">
                {formatScalePercent(draftScale)}
              </span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-zinc-600 sm:text-sm">{t('settings.fontHint')}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-3 sm:rounded-xl sm:p-5">
        <div className="mb-3 flex items-center gap-1.5 sm:mb-4 sm:gap-2">
          <SlidersHorizontal className="h-3.5 w-3.5 text-violet-600 sm:h-4 sm:w-4" />
          <p className="text-xs font-semibold text-zinc-900 sm:text-sm">{t('settings.fontFineTune')}</p>
        </div>
        <input
          type="range"
          min={FONT_SCALE_MIN}
          max={FONT_SCALE_MAX}
          step={FONT_SCALE_STEP}
          value={draftScale}
          onChange={(e) => onSlider(Number(e.target.value))}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-zinc-200 accent-emerald-600 sm:h-2"
          aria-label={t('settings.fontFineTune')}
        />
        <div className="mt-1.5 flex justify-between text-[10px] font-medium text-zinc-500 sm:mt-2 sm:text-xs">
          <span>{formatScalePercent(FONT_SCALE_MIN)}</span>
          <span className="text-emerald-700">{t('settings.fontCurrent', { percent: formatScalePercent(draftScale) })}</span>
          <span>{formatScalePercent(FONT_SCALE_MAX)}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
        {FONT_PRESETS.map((opt) => {
          const on = draft.mode === 'preset' && draft.preset === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => selectPreset(opt.id)}
              className={`flex flex-col items-start rounded-lg border p-2.5 text-left transition-all sm:rounded-xl sm:p-4 ${
                on
                  ? 'border-emerald-400 bg-emerald-50/80 ring-1 ring-emerald-200 sm:ring-2'
                  : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              <span className="flex w-full items-center justify-between gap-1.5">
                <span className="text-xs font-semibold text-zinc-900 sm:text-sm">{t(opt.labelKey)}</span>
                {on ? <Check className="h-4 w-4 shrink-0 text-emerald-600 sm:h-5 sm:w-5" /> : null}
              </span>
              <span className="mt-1.5 hidden text-xs leading-relaxed text-zinc-600 sm:mt-2 sm:block sm:text-sm">
                {t('settings.fontPreviewLine')}
              </span>
              <span
                className="mt-1 text-[10px] text-zinc-500 sm:hidden"
                style={{ fontSize: `calc(0.8125rem * ${Math.min(opt.mobileScale, 1.06)})` }}
              >
                ₹2,940
              </span>
              <span
                className="mt-1 hidden text-xs text-zinc-500 sm:inline"
                style={{ fontSize: `calc(0.875rem * ${opt.mobileScale})` }}
              >
                ₹2,940 · {t('nav.customers')}
              </span>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 sm:rounded-xl sm:p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 sm:text-xs sm:tracking-[0.12em]">
          {t('settings.fontLivePreview')}
        </p>
        <div className="mt-2 rounded-lg border border-zinc-200 bg-white p-3 text-sm shadow-sm sm:mt-3 sm:p-4">
          <div className="sm:hidden">
            <p className="text-xs font-semibold text-zinc-900">{t('nav.dashboard')}</p>
            <p className="mt-0.5 text-[11px] text-zinc-600">{t('settings.fontPreviewLine')}</p>
            <p className="mt-1.5 text-sm font-bold text-emerald-700">₹2,94,000</p>
          </div>
          <div className="hidden sm:block" style={{ fontSize: `calc(1rem * ${draftScale})` }}>
            <p className="text-sm font-semibold text-zinc-900">{t('nav.dashboard')}</p>
            <p className="mt-1 text-zinc-600">{t('settings.fontPreviewLine')}</p>
            <p className="mt-2 text-lg font-bold text-emerald-700">₹2,94,000</p>
          </div>
          <span className="mt-2 inline-flex rounded-md bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold text-white sm:mt-3 sm:px-3 sm:py-1.5 sm:text-xs">
            {t('common.save')}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!isDirty}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={JSON.stringify(draft) === JSON.stringify(DEFAULT_PREF)}
          className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-1.5 sm:px-4 sm:py-2 sm:text-sm"
        >
          <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          {t('settings.fontReset')}
        </button>
        {!isDirty && savedScale !== presetMetrics('comfortable').scale ? (
          <span className="self-center text-[10px] text-zinc-500 sm:text-xs">
            {t('settings.fontSavedAt', { percent: formatScalePercent(savedScale) })}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default FontSettings;
